import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCampaignDetails } from '../../../../../services/getCampaignDetails';
import { useSelector } from 'react-redux';
import { getToken } from '../../../../../features/token/tokenSaver';
import './AdvertiserCampaignDetails.css';
import { toggleAdvertismentAdvertiser } from '../../../../../services/toggleAdvertismentActiveAdvertiser';
import { deleteAdvertisementCampaign } from '../../../../../services/deleteAdvertismentCampaign';
import { resubmitCampaignContent } from '../../../../../services/resubmitCampaignContent';
import { useCampaignCreativeUpload } from '../../../../../hooks/useCampaignCreativeUpload';
import CampaignContentUploadForm from '../../../../../components/CampaignContentUploadForm/CampaignContentUploadForm';
import CampaignCreativePreview from '../../../../../components/CampaignCreativePreview/CampaignCreativePreview';

const ADVERTISER_RESUBMIT_INPUT_IDS = {
    horizontal: 'advertiserResubmitHorizontal',
    vertical: 'advertiserResubmitVertical',
};

function getSubscriptionId(data) {
    return data?.SubscriptionID ?? data?.SubscriptionId ?? '';
}

/** PayPal Billing Plans subscription id stored in SubscriptionID */
function isPayPalBillingSubscriptionIdUi(value) {
    const s = String(value || '').trim();
    return s.length >= 3 && /^I-[A-Z0-9]+$/i.test(s);
}

function hasBillingReference(data) {
    return String(getSubscriptionId(data)).trim().length > 0;
}

function hasPaidMonthlyFee(data) {
    const fee = Number(data?.MonthlyFee);
    return Number.isFinite(fee) && fee > 0;
}

function shouldShowRecurringBillingCallout(data) {
    return hasBillingReference(data) || hasPaidMonthlyFee(data);
}

function isCampaignEnded(data) {
    const endRaw = data?.SubscriptionEndDate;
    if (!endRaw) return false;
    const end = new Date(endRaw);
    if (Number.isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
}

export default function AdvertiserCampaignDetails() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const token = useSelector(getToken);
    const [adData, setAdData] = useState({});
    const [targetedScreens, setTargetedScreens] = useState([]);
    const [attributedSpendTotal, setAttributedSpendTotal] = useState(0);
    const [totalPlays, setTotalPlays] = useState(0);
    const [perPlayValueUsd, setPerPlayValueUsd] = useState(0);
    const [screenPerformance, setScreenPerformance] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showResubmitForm, setShowResubmitForm] = useState(false);
    const [resubmitError, setResubmitError] = useState('');
    const [resubmitSubmitting, setResubmitSubmitting] = useState(false);
    const resubmitUpload = useCampaignCreativeUpload(ADVERTISER_RESUBMIT_INPUT_IDS);
    const currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    const statusLabelMap = {
        1: 'Active',
        2: 'Inactive',
        3: 'Pending Approval',
    };

    function loadCampaignDetails(options = {}) {
        const silent = options.silent === true;
        if (!silent) {
            setLoading(true);
            setLoadError('');
        }

        getCampaignDetails(token, state?.adId)
            .then((response) => {
                setAdData(response.data?.advertismentData || {});
                setTargetedScreens(response.data?.targetedScreens || []);
                setAttributedSpendTotal(response.data?.attributedSpendTotal || 0);
                setTotalPlays(response.data?.totalPlays || 0);
                setPerPlayValueUsd(response.data?.perPlayValueUsd || 0);
                setScreenPerformance(response.data?.screenPerformance || []);
            })
            .catch(() => {
                if (!silent) {
                    setLoadError('Unable to load campaign details right now. Please try again.');
                }
            })
            .finally(() => {
                if (!silent) {
                    setLoading(false);
                }
            });
    }

    useEffect(() => {
        if (!state?.adId) {
            navigate(-1);
            return;
        }
        loadCampaignDetails();
    }, [state?.adId, token, navigate]);

    function formatDate(dateValue) {
        if (!dateValue) return 'N/A';
        const date = new Date(dateValue);
        if (Number.isNaN(date.getTime())) return 'N/A';
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    }

    function formatCurrency(value) {
        if (value === null || value === undefined || value === '') return 'N/A';
        return currencyFormatter.format(Number(value));
    }

    function getStatusLabel(statusId) {
        return statusLabelMap[statusId] || 'Unknown';
    }

    function toggleCampaignStatus() {
        const currentStatus = adData.StatusId;
        const newStatus = currentStatus === 1 ? 2 : 1;

        if (newStatus === 2) {
            let msg =
                'Deactivate this campaign? It will stop showing on screens. You can activate it again later.';
            if (isPayPalBillingSubscriptionIdUi(getSubscriptionId(adData))) {
                msg +=
                    ' Your PayPal subscription will be suspended until you activate this campaign again.';
            }
            if (!window.confirm(msg)) return;
        } else {
            const msg =
                'Set this campaign to active? It will be eligible to show again according to your plan and approval status.';
            if (!window.confirm(msg)) return;
        }

        setActionLoading(true);
        toggleAdvertismentAdvertiser(token, adData.AdvertisementId, String(newStatus))
            .then(() => {
                loadCampaignDetails({ silent: true });
            })
            .catch(() => {
                window.alert('Unable to update campaign status right now. Please try again.');
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    function deleteAdvertisement() {
        let msg = `Permanently delete campaign #${adData.AdvertisementId}? This cannot be undone.`;
        if (isPayPalBillingSubscriptionIdUi(getSubscriptionId(adData))) {
            msg +=
                ' Your PayPal subscription for this campaign will be canceled and you will not be charged again for it.';
        }
        const confirmed = window.confirm(msg);
        if (!confirmed) {
            return;
        }
        setActionLoading(true);
        deleteAdvertisementCampaign(token, adData.AdvertisementId)
            .then(() => {
                navigate('/advertiserDashboard/campaigns');
            })
            .catch(() => {
                window.alert('Unable to delete this campaign right now. Please try again.');
            })
            .finally(() => {
                setActionLoading(false);
            });
    }

    if (loading) {
        return (
            <div className='advertiserDashboardContainerCampaigns'>
                <div className='adminDashboardScreenPageContainer'>
                    <div className='campaignDetailsStateCard'>Loading campaign details...</div>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className='advertiserDashboardContainerCampaigns'>
                <div className='adminDashboardScreenPageContainer'>
                    <div className='campaignDetailsStateCard error'>{loadError}</div>
                </div>
            </div>
        );
    }

    const pendingApproval = adData.StatusId === 3;
    const isPrepaidReserved = adData.SubscriptionStatus === 'prepaid_reserved';
    const campaignEnded = isCampaignEnded(adData);
    const canRenew = campaignEnded
        && !isPrepaidReserved
        && !pendingApproval
        && Boolean(adData.AdContentHorizontal && adData.AdContentVertical);
    const isSelectedScreens = adData.TargetingMode === 'selected_screens';
    const subscriptionStatus = String(adData.SubscriptionStatus || '').toLowerCase();
    const hasPendingRevision = Boolean(adData.PendingAdContentHorizontal && adData.PendingAdContentVertical);
    const canResubmitCreatives = (subscriptionStatus === 'active' || subscriptionStatus === 'suspended')
        && !hasPendingRevision
        && Boolean(adData.AdContentHorizontal && adData.AdContentVertical)
        && !campaignEnded;

    async function handleResubmitCreatives(payload) {
        setResubmitError('');
        setResubmitSubmitting(true);
        try {
            await resubmitCampaignContent(adData.AdvertisementId, payload, token);
            alert('New creatives submitted for review. Your current assets stay live until approved.');
            setShowResubmitForm(false);
            resubmitUpload.resetCreatives();
            loadCampaignDetails({ silent: true });
        } catch (err) {
            setResubmitError(err.response?.data?.error || 'Unable to submit new creatives.');
        } finally {
            setResubmitSubmitting(false);
        }
    }

    return (
        <div className='advertiserDashboardContainerCampaigns'>
            <div className='adminDashboardScreenPageContainer'>
                <h1 className='advertismentCampaignDetailsTitle'>Campaign Details</h1>

                {state?.renewSuccess && (
                    <div className='campaignBillingNotice' role='status' style={{ marginBottom: '1.25rem' }}>
                        <p style={{ margin: 0 }}>
                            Campaign renewed successfully
                            {state.emailSent === false
                                ? '. Payment went through; if you do not see a receipt email, check spam or contact support.'
                                : '. Your ads are active again for another month.'}
                        </p>
                    </div>
                )}

                <div className='campaignDetailsHeaderCard'>
                    <div>
                        <div className='campaignName'>{adData.AdvertisementName || 'Untitled Campaign'}</div>
                        <div className='campaignMeta'>Campaign #{adData.AdvertisementId}</div>
                    </div>
                    <span className={`campaignStatusBadge status-${campaignEnded ? 2 : (adData.StatusId || 0)}`}>
                        {isPrepaidReserved
                            ? 'Prepaid — upload creative'
                            : campaignEnded
                                ? 'Ended'
                                : getStatusLabel(adData.StatusId)}
                    </span>
                </div>

                {canRenew && (
                    <div className='campaignBillingNotice' role='note' style={{ marginBottom: '1.25rem' }}>
                        <p style={{ margin: '0 0 0.75rem' }}>
                            This campaign ended on {formatDate(adData.SubscriptionEndDate)}. Renew with a one-time
                            payment to run for another month with the same creatives and screens.
                        </p>
                        <button
                            type='button'
                            className='approveBtn userDetailsBtn'
                            onClick={() => navigate('/advertiserDashboard/renewCampaign', {
                                state: { adId: adData.AdvertisementId },
                            })}
                        >
                            Renew campaign
                        </button>
                    </div>
                )}

                {isPrepaidReserved && (
                    <div className='campaignBillingNotice' role='note' style={{ marginBottom: '1.25rem' }}>
                        <p style={{ margin: '0 0 0.75rem' }}>
                            Your spot is reserved for {formatDate(adData.SubscriptionStartDate)}. Upload horizontal and
                            vertical creative to submit this campaign for approval.
                        </p>
                        <button
                            type='button'
                            className='approveBtn userDetailsBtn'
                            onClick={() => navigate('/advertiserDashboard/completePrepaid', { state: { adId: adData.AdvertisementId } })}
                        >
                            Upload campaign creative
                        </button>
                    </div>
                )}

                <div className='campaignSummaryGrid'>
                    <div className='campaignSummaryCard'>
                        <span className='summaryLabel'>Placement</span>
                        <span className='summaryValue'>
                            {isSelectedScreens && targetedScreens.length > 0
                                ? `${targetedScreens.length} selected screen${targetedScreens.length === 1 ? '' : 's'}`
                                : 'Network (all screens)'}
                        </span>
                    </div>
                    <div className='campaignSummaryCard'>
                        <span className='summaryLabel'>Monthly Fee</span>
                        <span className='summaryValue'>{formatCurrency(adData.MonthlyFee)}</span>
                    </div>
                    <div className='campaignSummaryCard'>
                        <span className='summaryLabel'>Subscription</span>
                        <span className='summaryValue'>{adData.SubscriptionStatus || 'N/A'}</span>
                    </div>
                    <div className='campaignSummaryCard'>
                        <span className='summaryLabel'>Storefront</span>
                        <span className='summaryValue'>{adData.Storefront ? 'Enabled' : 'Disabled'}</span>
                    </div>
                </div>

                <div className='campaignDetailsSection'>
                    <h2 className='campaignSectionTitle'>Delivery & Spend</h2>
                    <div className='campaignSummaryGrid'>
                        <div className='campaignSummaryCard'>
                            <span className='summaryLabel'>Total plays</span>
                            <span className='summaryValue'>{totalPlays.toLocaleString()}</span>
                        </div>
                        <div className='campaignSummaryCard'>
                            <span className='summaryLabel'>Attributed spend</span>
                            <span className='summaryValue'>{formatCurrency(attributedSpendTotal)}</span>
                        </div>
                        <div className='campaignSummaryCard'>
                            <span className='summaryLabel'>Per-play value</span>
                            <span className='summaryValue'>{formatCurrency(perPlayValueUsd)}</span>
                        </div>
                    </div>
                    {screenPerformance.length > 0 && (
                        <table className='topCampaignsTable' style={{ marginTop: '1rem' }}>
                            <thead>
                                <tr>
                                    <th>Screen</th>
                                    <th>Plays</th>
                                    <th>Attributed spend</th>
                                </tr>
                            </thead>
                            <tbody>
                                {screenPerformance.map((row) => (
                                    <tr key={row.ScreenId}>
                                        <td>#{row.ScreenId}</td>
                                        <td>{row.ImpressionCount ?? row.playCount ?? 0}</td>
                                        <td>{formatCurrency(row.attributedSpend ?? 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className='campaignDetailsSection'>
                    <h2 className='campaignSectionTitle'>Subscription Window</h2>
                    <div className='campaignDataList'>
                        <div className='campaignDataItem'>
                            <span className='campaignDataLabel'>Start Date</span>
                            <span className='campaignDataValue'>{formatDate(adData.SubscriptionStartDate)}</span>
                        </div>
                        <div className='campaignDataItem'>
                            <span className='campaignDataLabel'>End Date</span>
                            <span className='campaignDataValue'>{formatDate(adData.SubscriptionEndDate)}</span>
                        </div>
                    </div>
                </div>

                <div className='campaignDetailsSection'>
                    <h2 className='campaignSectionTitle'>Creative Assets</h2>
                    {hasPendingRevision && (
                        <p className='campaignBillingNotice' role='status' style={{ marginBottom: '1rem' }}>
                            New creatives pending review — your current assets below remain live on screens until INVERT approves the update.
                        </p>
                    )}
                    <div className='advertismentImagesContainer'>
                        {adData.AdContentHorizontal ? (
                            <CampaignCreativePreview
                                src={adData.AdContentHorizontal}
                                alt='Horizontal ad content'
                                slot='horizontal'
                                showLabel
                            />
                        ) : (
                            <div className='creativePlaceholder'>No horizontal asset</div>
                        )}
                        {adData.AdContentVertical ? (
                            <CampaignCreativePreview
                                src={adData.AdContentVertical}
                                alt='Vertical ad content'
                                slot='vertical'
                                showLabel
                            />
                        ) : (
                            <div className='creativePlaceholder'>No vertical asset</div>
                        )}
                    </div>
                </div>

                {hasPendingRevision && (
                    <div className='campaignDetailsSection'>
                        <h2 className='campaignSectionTitle'>Pending Revision Preview</h2>
                        <div className='advertismentImagesContainer'>
                            <CampaignCreativePreview
                                src={adData.PendingAdContentHorizontal}
                                alt='Pending horizontal'
                                slot='horizontal'
                                showLabel
                            />
                            <CampaignCreativePreview
                                src={adData.PendingAdContentVertical}
                                alt='Pending vertical'
                                slot='vertical'
                                showLabel
                            />
                        </div>
                    </div>
                )}

                {(canResubmitCreatives || showResubmitForm) && (
                    <div className='campaignDetailsSection'>
                        <h2 className='campaignSectionTitle'>Submit New Creatives</h2>
                        {!showResubmitForm ? (
                            <>
                                <p className='campaignDataValue'>
                                    Upload replacement horizontal and vertical assets for admin review. Live creatives stay on screens until approved.
                                </p>
                                <button
                                    type='button'
                                    className='approveBtn userDetailsBtn'
                                    onClick={() => {
                                        setResubmitError('');
                                        setShowResubmitForm(true);
                                    }}
                                >
                                    Submit new creatives
                                </button>
                            </>
                        ) : (
                            <CampaignContentUploadForm
                                inputIds={ADVERTISER_RESUBMIT_INPUT_IDS}
                                creativeHook={resubmitUpload}
                                submitLabel='Submit for review'
                                submitting={resubmitSubmitting}
                                error={resubmitError}
                                onSubmit={handleResubmitCreatives}
                                onCancel={() => {
                                    setShowResubmitForm(false);
                                    setResubmitError('');
                                    resubmitUpload.resetCreatives();
                                }}
                            />
                        )}
                    </div>
                )}

                <div className='campaignDetailsSection'>
                    <h2 className='campaignSectionTitle'>Actions</h2>
                    <p className='campaignDataValue'>
                        {isPayPalBillingSubscriptionIdUi(getSubscriptionId(adData)) ? (
                            <>
                                <strong>Deactivate</strong> pauses your campaign and <strong>suspends</strong>{' '}
                                PayPal billing until you activate it again. <strong>Delete</strong> permanently
                                removes the campaign and <strong>cancels</strong> your PayPal subscription for it.
                            </>
                        ) : hasBillingReference(adData) ? (
                            <>
                                <strong>Deactivate</strong> pauses your campaign on screens.{' '}
                                <strong>Delete</strong> permanently removes the campaign. Card or other payment
                                references on file do not automatically charge again for this one-time setup;
                                contact support if you have billing questions.
                            </>
                        ) : (
                            <>
                                <strong>Deactivate</strong> pauses your campaign on screens.{' '}
                                <strong>Delete</strong> permanently removes the campaign.
                            </>
                        )}
                    </p>
                    {shouldShowRecurringBillingCallout(adData) ? (
                        <div className='campaignBillingNotice' role='note'>
                            {isPayPalBillingSubscriptionIdUi(getSubscriptionId(adData)) ? (
                                <p style={{ margin: 0 }}>
                                    <strong>Recurring billing (PayPal):</strong> Deactivating suspends that
                                    subscription; deleting cancels it permanently.
                                </p>
                            ) : hasBillingReference(adData) ? (
                                <p style={{ margin: 0 }}>
                                    <strong>Billing on file:</strong> This campaign has a payment reference (for
                                    example Stripe). Deactivate or delete updates your campaign in Invert; it does
                                    not create new charges for a completed one-time payment. Future subscription
                                    billing may use Stripe—watch for product updates.
                                </p>
                            ) : (
                                <p style={{ margin: 0 }}>
                                    <strong>Monthly fee:</strong> This campaign has a monthly fee on record. If you
                                    use PayPal for subscription billing, deactivating suspends charges until you
                                    activate again; deleting cancels the subscription.
                                </p>
                            )}
                        </div>
                    ) : null}

                    <div className='campaignActionsRow'>
                        <span className='campaignDataValue'>
                            Current status:{' '}
                            <span className={`campaignStatusBadge status-${campaignEnded ? 2 : (adData.StatusId || 0)}`}>
                                {campaignEnded ? 'Ended' : getStatusLabel(adData.StatusId)}
                            </span>
                        </span>
                        {canRenew && (
                            <button
                                type='button'
                                className='approveBtn userDetailsBtn'
                                onClick={() => navigate('/advertiserDashboard/renewCampaign', {
                                    state: { adId: adData.AdvertisementId },
                                })}
                            >
                                Renew campaign
                            </button>
                        )}
                        <button
                            type='button'
                            onClick={toggleCampaignStatus}
                            className='approveBtn userDetailsBtn'
                            disabled={actionLoading || pendingApproval || isPrepaidReserved || campaignEnded}
                            title={
                                campaignEnded
                                    ? 'Renew this campaign to run it again'
                                    : isPrepaidReserved
                                        ? 'Upload creative before activating'
                                        : pendingApproval
                                            ? 'Cannot change status while pending approval'
                                            : ''
                            }
                        >
                            {actionLoading
                                ? 'Processing...'
                                : adData.StatusId === 1
                                  ? 'Deactivate campaign'
                                  : 'Activate campaign'}
                        </button>
                    </div>

                    <button
                        type='button'
                        className='denyBtnCampaignDetails userDetailsBtn'
                        onClick={deleteAdvertisement}
                        disabled={actionLoading}
                    >
                        {actionLoading ? 'Processing...' : 'Delete campaign'}
                    </button>
                </div>
            </div>
        </div>
    );
}
